import { Component, EventEmitter, Input, OnChanges, Output } from '@angular/core';
import { QUIZ_CHOICE_SLOT, Quiz, QuizOption } from '../../model/quiz';

type QuizState = 'answering' | 'checked';

/**
 * One quiz exercise (see model/quiz.ts): choose, fill the gaps, match or type, then check the answers or
 * reveal them.
 */
@Component({
  selector: 'app-quiz',
  templateUrl: './quiz.component.html',
  styleUrls: ['./quiz.component.scss'],
  standalone: false
})
export class QuizComponent implements OnChanges {

  @Input() quiz: Quiz | null = null;
  // Emitted by the OK button shown after a correct answer — the host closes the modal.
  @Output() done = new EventEmitter<void>();

  // Chosen option ids of a select exercise.
  chosen: string[] = [];
  // Value per slot (gap id / item number) of gap, matching and blank exercises.
  values: Record<string, string> = {};
  state: QuizState = 'answering';

  ngOnChanges(): void {
    this.reset();
  }

  get isSelect(): boolean {
    return this.quiz?.type === 'single_select' || this.quiz?.type === 'multi_select';
  }

  get multiple(): boolean {
    return this.quiz?.type === 'multi_select';
  }

  get checked(): boolean {
    return this.state === 'checked';
  }

  reset(): void {
    this.chosen = [];
    this.values = this.defaultValues();
    this.state = 'answering';
  }

  // Selects start empty (only the arrow is shown): every select has a leading empty option.
  private defaultValues(): Record<string, string> {
    const values: Record<string, string> = {};
    if (!this.quiz) return values;
    if (this.quiz.type === 'gap_fill' || this.quiz.type === 'wordpool') {
      this.quiz.prompt.filter(segment => segment.kind === 'gap').forEach(segment => values[segment.value] = '');
    } else if (this.quiz.type === 'matching') {
      this.quiz.items.forEach(item => values[item.n] = '');
    }
    return values;
  }

  // Check is enabled once every gap / statement has an answer (blank_fill and the choice lists are not forced).
  get canCheck(): boolean {
    if (!this.quiz) return false;
    if (this.quiz.type === 'gap_fill' || this.quiz.type === 'wordpool' || this.quiz.type === 'matching') {
      return Object.values(this.values).every(value => !!value);
    }
    return true;
  }

  toggle(option: QuizOption): void {
    if (this.checked) return;
    if (!this.multiple) this.chosen = [option.id];
    else this.chosen = this.chosen.includes(option.id) ? this.chosen.filter(id => id !== option.id) : [...this.chosen, option.id];
  }

  verify(): void {
    this.state = 'checked';
  }

  showAnswers(): void {
    if (!this.quiz) return;
    this.chosen = [...(this.quiz.answers.get(QUIZ_CHOICE_SLOT) ?? [])];
    this.values = this.defaultValues();
    this.quiz.answers.forEach((answers, key) => {
      if (key !== QUIZ_CHOICE_SLOT) this.values[key] = answers[0];
    });
    this.state = 'checked';
  }

  isCorrectOption(option: QuizOption): boolean {
    return (this.quiz?.answers.get(QUIZ_CHOICE_SLOT) ?? []).includes(option.id);
  }

  // Feedback class of a select option after checking: right or wrong pick. A missed correct option is deliberately
  // not marked, so "Try again" stays a genuine second attempt.
  optionClass(option: QuizOption): string {
    if (!this.checked) return '';
    const picked = this.chosen.includes(option.id);
    const correct = this.isCorrectOption(option);
    if (picked && correct) return 'is-correct';
    return picked ? 'is-wrong' : '';
  }

  // "c) Always stay aware of …" for the correct option of a slot (matching feedback).
  correctAnswerLabel(key: string): string {
    const id = this.quiz?.answers.get(key)?.[0];
    const option = this.quiz?.options.find(candidate => candidate.id === id);
    return option ? `${option.id}) ${option.text}` : '';
  }

  // Feedback class of a gap / matching / blank slot after checking.
  slotClass(key: string): string {
    if (!this.checked) return '';
    return this.isSlotCorrect(key) ? 'is-correct' : 'is-wrong';
  }

  get allCorrect(): boolean {
    if (!this.quiz) return false;
    if (this.isSelect) {
      const correct = this.quiz.answers.get(QUIZ_CHOICE_SLOT) ?? [];
      return correct.length === this.chosen.length && correct.every(id => this.chosen.includes(id));
    }
    return Array.from(this.quiz.answers.keys()).every(key => this.isSlotCorrect(key));
  }

  private isSlotCorrect(key: string): boolean {
    const given = (this.values[key] ?? '').trim().toLowerCase();
    return !!given && (this.quiz?.answers.get(key) ?? []).some(answer => answer.trim().toLowerCase() === given);
  }
}
